/**
 * /tools/nis2 — NIS2 readiness self-assessment.
 * 15-question Likert quiz (4 areas) → server scores → PDF report.
 */
import React, { useEffect, useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
    ShieldCheck, ArrowRight, ArrowLeft, Loader2, Download, Award,
    FileText, Sparkles, ListChecks, Building2, Calendar, ChevronDown, ChevronUp,
} from "lucide-react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { getJSON, postJSON } from "../lib/api";
import { usePageMeta } from "../lib/usePageMeta";
import { ToolPageMeta } from "../components/ToolPageMeta";
import { SEO, SITE_DOMAIN } from "../components/SEO";
import { useAuthStore } from "../state/authStore";

const SCALE = [
    { v: 0, label: "Not at all",            help: "No process exists." },
    { v: 1, label: "Aware",                 help: "We've discussed it." },
    { v: 2, label: "Partially implemented", help: "Some teams, ad-hoc." },
    { v: 3, label: "Mostly implemented",    help: "Documented, in use." },
    { v: 4, label: "Fully implemented",     help: "Operationalized + reviewed." },
];

const AREA_LABEL = {
    governance: "Governance & Risk Management",
    incident: "Incident Handling & Continuity",
    supply_chain: "Supply Chain Security",
    technical: "Technical Measures",
};

const MATURITY_TONE = {
    Initial:     { text: "text-accent-red",   ring: "ring-accent-red/40",   bg: "bg-accent-red/[0.05]"   },
    Developing:  { text: "text-accent-amber", ring: "ring-accent-amber/40", bg: "bg-accent-amber/[0.05]" },
    Defined:     { text: "text-accent-cyan",  ring: "ring-accent-cyan/40",  bg: "bg-accent-cyan/[0.05]"  },
    Managed:     { text: "text-accent-mint",  ring: "ring-accent-mint/40",  bg: "bg-accent-mint/[0.05]"  },
    Optimized:   { text: "text-accent-mint",  ring: "ring-accent-mint/40",  bg: "bg-accent-mint/[0.10]"  },
};

const FAQ = [
    {
        q: "Who must comply with NIS2?",
        a: "Medium and large entities (≥50 staff OR ≥€10M turnover) operating in 18 'essential' or 'important' sectors — energy, transport, banking, health, water, digital infrastructure, public administration, manufacturing, postal, food, chemicals, research, space, ICT service management, MSP/MSSPs, etc. Member-state transposition deadlines vary; Italy's national law landed in late 2024.",
    },
    {
        q: "What are the sanctions?",
        a: "Essential entities: up to €10M or 2% global turnover. Important entities: up to €7M or 1.4% global turnover. Management can be personally liable for the absence of a cybersecurity policy or risk-management measures.",
    },
    {
        q: "How fast must I report an incident?",
        a: "Early warning within 24h, incident notification within 72h, and a final report within 1 month. The clock starts when the entity becomes aware of the significant incident.",
    },
    {
        q: "What does 'good' look like for NIS2 Article 21?",
        a: "Risk-management plan, incident response, business continuity, supply chain security, secure development/acquisition, cyber hygiene + training, crypto + access control, MFA. Our 15-question quiz maps directly to those measures.",
    },
];

export const Nis2 = () => {
    usePageMeta({
        title: "NIS2 Readiness Self-Assessment — Free 15-question audit",
        description:
            "Free NIS2 Directive readiness assessment. 15 questions across 4 macro-areas. Get a score 0-100, maturity level, top-5 prioritized recommendations and a downloadable PDF roadmap.",
        jsonLd: {
            "@context": "https://schema.org", "@type": "Quiz",
            name: "NIS2 Readiness Self-Assessment", educationalAlignment: { "@type": "AlignmentObject", alignmentType: "frameworkAlignment", targetName: "EU NIS2 Directive" },
        },
    });

    const [questions, setQuestions] = useState([]);
    const [step, setStep] = useState(0); // 0..14 question, 15 = contact, 16 = results
    const [answers, setAnswers] = useState({});
    const [contact, setContact] = useState({ name: "", email: "", company: "" });
    const [busy, setBusy] = useState(false);
    const [result, setResult] = useState(null);
    const [openFaq, setOpenFaq] = useState(null);
    const [pdfGateOpen, setPdfGateOpen] = useState(false);
    const [pdfGateForm, setPdfGateForm] = useState({ email: "", name: "", company: "" });
    const [pdfGateBusy, setPdfGateBusy] = useState(false);
    const [gateSuccess, setGateSuccess] = useState(null);
    const { user } = useAuthStore();

    useEffect(() => {
        getJSON("/tools/nis2/questions").then((d) => setQuestions(d.questions || [])).catch(() => {});
    }, []);

    const progress = Math.min(100, Math.round((step / 16) * 100));
    const cur = questions[step];

    const setAns = (v) => {
        if (!cur) return;
        const nextAnswers = { ...answers, [cur.id]: v };
        setAnswers(nextAnswers);
        // Phase 7a — after the last question, go straight to RESULTS (no pre-quiz
        // contact form). For anonymous users the inline results render immediately
        // and the email gate appears only when they click "Download PDF".
        if (Object.keys(nextAnswers).length === 15) {
            // Auto-submit with no email — server will return score/recs but NO PDF
            // (gated:true) for anonymous, OR full PDF for authenticated users.
            setTimeout(() => submitAuto(nextAnswers), 280);
        } else {
            setTimeout(() => setStep((s) => Math.min(s + 1, 14)), 250);
        }
    };

    const submitAuto = async (finalAnswers) => {
        setBusy(true);
        try {
            const r = await postJSON("/tools/nis2/assessment", { answers: finalAnswers });
            setResult(r);
            setStep(16);
        } catch (e) {
            toast.error(e?.message || "Could not submit assessment");
        } finally {
            setBusy(false);
        }
    };

    const submit = async (skipContact = false) => {
        setBusy(true);
        try {
            const payload = {
                answers,
                contact: skipContact ? undefined : (contact.email ? contact : undefined),
                send_email: !skipContact && !!contact.email,
            };
            const r = await postJSON("/tools/nis2/assessment", payload);
            setResult(r);
            setStep(16);
            if (r?.email_sent && contact.email) {
                setGateSuccess(contact.email);
                toast.success(`Report emailed to ${contact.email}`);
            }
        } catch (e) {
            toast.error(e?.message || "Could not submit assessment");
        } finally {
            setBusy(false);
        }
    };

    const downloadPdf = () => {
        if (!result?.pdf_base64) return;
        const blob = new Blob(
            [Uint8Array.from(atob(result.pdf_base64), (c) => c.charCodeAt(0))],
            { type: "application/pdf" },
        );
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `cybersecurity-forefront-nis2-report-${new Date().toISOString().slice(0, 10)}.pdf`;
        document.body.appendChild(a);
        a.click(); a.remove();
        URL.revokeObjectURL(url);
        toast.success("PDF downloaded");
    };

    // Phase 7a — anonymous email gate before downloading the PDF.
    const submitGate = async (e) => {
        e?.preventDefault();
        const email = pdfGateForm.email.trim().toLowerCase();
        if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email)) {
            toast.error("Enter a valid email."); return;
        }
        setPdfGateBusy(true);
        try {
            const r = await postJSON("/tools/nis2/assessment", {
                answers,
                contact: { name: pdfGateForm.name || null, email, company: pdfGateForm.company || null },
                send_email: true,
            });
            setResult(r);
            setGateSuccess(email);
            setPdfGateOpen(false);
            // Trigger download with the newly-returned base64
            const blob = new Blob(
                [Uint8Array.from(atob(r.pdf_base64), (c) => c.charCodeAt(0))],
                { type: "application/pdf" },
            );
            const url = URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url;
            a.download = `cybersecurity-forefront-nis2-report-${new Date().toISOString().slice(0, 10)}.pdf`;
            document.body.appendChild(a);
            a.click(); a.remove();
            URL.revokeObjectURL(url);
        } catch (e) {
            toast.error(e?.message || "Could not unlock the PDF.");
        } finally {
            setPdfGateBusy(false);
        }
    };

    return (
        <div className="pt-24 md:pt-28 pb-16" data-testid="nis2-page">
            <SEO
                title="NIS2 Self-Assessment — Free 10-question scoring tool"
                description="Free NIS2 readiness self-assessment. 10 questions, instant scoring, evidence checklist, downloadable PDF for your board. Built by senior security engineers."
                canonicalPath="/tools/nis2-assessment"
            />
            <div className="max-w-3xl mx-auto px-5">
                <ToolPageMeta toolId="nis2-assessment" />
                <p className="text-[10.5px] font-mono uppercase tracking-[0.32em] text-accent-cyan">Free assessment</p>
                <h1 className="mt-2 font-display font-semibold text-4xl sm:text-5xl tracking-tight">
                    NIS2 readiness in 5 minutes.
                </h1>
                <p className="mt-3 text-[14px] text-text-muted leading-relaxed max-w-xl">
                    15 questions across governance, incident handling, supply-chain and technical measures.
                    Get a 0-100 score, maturity level, and a personalized PDF roadmap.
                </p>

                {/* Progress */}
                <div className="mt-7 h-1.5 w-full bg-white/[0.05] rounded overflow-hidden" data-testid="nis2-progress">
                    <div className="h-full bg-accent-cyan transition-all" style={{ width: `${progress}%` }} />
                </div>
                <p className="mt-1 text-[10.5px] font-mono uppercase tracking-[0.22em] text-text-dim">
                    {step <= 14 ? `Question ${step + 1}/15` : "Results"}
                </p>

                {/* Question step */}
                <AnimatePresence mode="wait">
                    {step <= 14 && cur && (
                        <motion.section
                            key={`q-${step}`}
                            initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }}
                            className="mt-7 rounded-xl border border-white/[0.06] bg-surface/60 p-6"
                            data-testid={`nis2-question-${step}`}>
                            <p className="text-[10.5px] font-mono uppercase tracking-[0.22em] text-accent-cyan mb-1">
                                {AREA_LABEL[cur.area]}
                            </p>
                            <h2 className="font-display font-semibold text-[18px] leading-snug">{cur.text}</h2>
                            <div className="mt-5 space-y-1.5">
                                {SCALE.map((s) => {
                                    const selected = answers[cur.id] === s.v;
                                    return (
                                        <button key={s.v} type="button" onClick={() => setAns(s.v)}
                                            data-testid={`nis2-answer-${cur.id}-${s.v}`}
                                            className={["w-full flex items-center gap-3 px-4 py-3 rounded-md border text-left transition-all",
                                                selected
                                                    ? "bg-accent-cyan/15 border-accent-cyan/50 text-accent-cyan"
                                                    : "bg-canvas border-white/[0.08] text-text-muted hover:border-white/[0.18]",
                                            ].join(" ")}>
                                            <span className={[
                                                "inline-flex h-7 w-7 items-center justify-center rounded-full border text-[11px] font-mono",
                                                selected ? "border-accent-cyan/50" : "border-white/[0.1]"
                                            ].join(" ")}>{s.v}</span>
                                            <span className="flex-1">
                                                <span className="block text-[14px] font-medium text-text-primary">{s.label}</span>
                                                <span className="block text-[11.5px] text-text-dim mt-0.5">{s.help}</span>
                                            </span>
                                        </button>
                                    );
                                })}
                            </div>
                            <div className="mt-5 flex justify-between items-center">
                                <button onClick={() => setStep(Math.max(0, step - 1))} disabled={step === 0}
                                    className="inline-flex items-center gap-1 text-[12.5px] text-text-muted hover:text-text-primary disabled:opacity-30"
                                    data-testid="nis2-back">
                                    <ArrowLeft className="w-3.5 h-3.5" /> Back
                                </button>
                                <span className="text-[10.5px] text-text-dim font-mono">
                                    {Object.keys(answers).length}/15 answered
                                </span>
                            </div>
                        </motion.section>
                    )}

                    {step === 15 && (
                        <motion.section
                            key="contact"
                            initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }}
                            className="mt-7 rounded-xl border border-white/[0.06] bg-surface/60 p-6"
                            data-testid="nis2-contact-step">
                            <h2 className="font-display font-semibold text-[18px]">Where should we send the PDF?</h2>
                            <p className="mt-1 text-[13px] text-text-muted">
                                All fields are optional — but adding your email also unlocks the downloadable PDF roadmap.
                            </p>
                            <div className="mt-5 grid grid-cols-1 sm:grid-cols-2 gap-3">
                                <Field label="Your name" value={contact.name} onChange={(v) => setContact({ ...contact, name: v })} testId="nis2-name" />
                                <Field label="Email" type="email" value={contact.email} onChange={(v) => setContact({ ...contact, email: v })} testId="nis2-email" />
                                <Field label="Company" value={contact.company} onChange={(v) => setContact({ ...contact, company: v })} testId="nis2-company" cls="sm:col-span-2" />
                            </div>
                            <div className="mt-5 flex justify-between flex-wrap gap-2">
                                <button onClick={() => setStep(14)}
                                    className="inline-flex items-center gap-1 px-3 py-1.5 text-[12.5px] text-text-muted hover:text-text-primary"
                                    data-testid="nis2-contact-back">
                                    <ArrowLeft className="w-3.5 h-3.5" /> Back to questions
                                </button>
                                <div className="flex gap-2">
                                    <button onClick={() => submit(true)} disabled={busy}
                                        className="px-4 py-2 rounded-md border border-white/[0.08] text-[12.5px] text-text-muted hover:text-text-primary disabled:opacity-50"
                                        data-testid="nis2-skip">
                                        Skip & see score
                                    </button>
                                    <button onClick={() => submit(false)} disabled={busy || Object.keys(answers).length < 15}
                                        data-testid="nis2-submit"
                                        className="inline-flex items-center gap-1.5 px-4 py-2 rounded-md bg-accent-cyan text-canvas text-[12.5px] font-semibold hover:shadow-glow-cyan disabled:opacity-50">
                                        {busy ? <><Loader2 className="w-3.5 h-3.5 animate-spin" /> Calculating…</> :
                                            <><Sparkles className="w-3.5 h-3.5" /> Submit & email me the PDF</>}
                                    </button>
                                </div>
                            </div>
                        </motion.section>
                    )}

                    {step === 16 && result && (
                        <motion.section key="results"
                            initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }}
                            className="mt-7 space-y-5"
                            data-testid="nis2-results">
                            {/* Score banner */}
                            <div className={`rounded-2xl border ${MATURITY_TONE[result.maturity_level]?.ring || "border-white/[0.08]"} ${MATURITY_TONE[result.maturity_level]?.bg || "bg-surface/40"} p-6 flex items-center gap-5 flex-wrap`}
                                data-testid="nis2-score-banner">
                                <div className="text-center">
                                    <div className={`font-display font-semibold text-6xl tabular-nums ${MATURITY_TONE[result.maturity_level]?.text || "text-text-primary"}`}>{result.score}</div>
                                    <div className={`text-[12px] font-mono uppercase tracking-[0.22em] ${MATURITY_TONE[result.maturity_level]?.text || "text-text-muted"}`}>{result.maturity_level}</div>
                                </div>
                                <div className="flex-1 min-w-[200px]">
                                    <p className="text-[11px] font-mono uppercase tracking-[0.22em] text-text-dim">Out of 100</p>
                                    <p className="text-[14px] text-text-muted leading-relaxed mt-1">
                                        Initial → Developing → Defined → Managed → Optimized. Where you are now and how to climb the ladder.
                                    </p>
                                </div>
                                {result.pdf_base64 ? (
                                    <button onClick={downloadPdf} data-testid="nis2-download-pdf"
                                        className="inline-flex items-center gap-1.5 px-4 py-2.5 rounded-md bg-accent-cyan text-canvas text-[13px] font-medium hover:shadow-glow-cyan">
                                        <Download className="w-3.5 h-3.5" /> Download PDF
                                    </button>
                                ) : (
                                    <button onClick={() => setPdfGateOpen(true)} data-testid="nis2-pdf-gate-open"
                                        className="inline-flex items-center gap-1.5 px-4 py-2.5 rounded-md bg-accent-cyan text-canvas text-[13px] font-medium hover:shadow-glow-cyan">
                                        <Download className="w-3.5 h-3.5" /> Download PDF
                                    </button>
                                )}
                            </div>

                            {/* Email gate confirmation (after submit) */}
                            {gateSuccess && (
                                <div className="rounded-xl border border-accent-mint/30 bg-accent-mint/[0.05] p-4 flex items-center gap-2" data-testid="nis2-gate-confirm">
                                    <span className="text-accent-mint text-[14px]">✓</span>
                                    <span className="text-[13px]">Report sent. We just emailed it to <span className="font-mono text-accent-mint">{gateSuccess}</span> too.</span>
                                </div>
                            )}

                            {/* Area bars */}
                            <div className="rounded-xl border border-white/[0.06] bg-surface/40 p-5">
                                <div className="flex items-center gap-2 mb-3">
                                    <Award className="w-4 h-4 text-accent-cyan" />
                                    <h3 className="font-display font-semibold text-[14px]">Area breakdown</h3>
                                </div>
                                <div className="space-y-3">
                                    {Object.entries(result.area_scores).map(([area, info]) => {
                                        const pct = Math.round((info.score / info.max) * 100);
                                        return (
                                            <div key={area} data-testid={`nis2-area-${area.toLowerCase().replace(/\W/g, '-')}`}>
                                                <div className="flex justify-between text-[12.5px] mb-1">
                                                    <span className="text-text-muted">{area}</span>
                                                    <span className="font-mono text-text-primary">{info.score}/{info.max} ({pct}%)</span>
                                                </div>
                                                <div className="h-1.5 w-full bg-white/[0.05] rounded overflow-hidden">
                                                    <div className={["h-full transition-all",
                                                        pct >= 80 ? "bg-accent-mint" : pct >= 50 ? "bg-accent-cyan" : pct >= 30 ? "bg-accent-amber" : "bg-accent-red",
                                                    ].join(" ")} style={{ width: `${pct}%` }} />
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>

                            {/* Recs */}
                            <div className="rounded-xl border border-white/[0.06] bg-surface/40 p-5">
                                <div className="flex items-center gap-2 mb-3">
                                    <ListChecks className="w-4 h-4 text-accent-cyan" />
                                    <h3 className="font-display font-semibold text-[14px]">Top-5 prioritized recommendations</h3>
                                </div>
                                <ol className="space-y-2.5">
                                    {result.recommendations.map((r) => (
                                        <li key={r.priority} className="flex gap-3 text-[13px]" data-testid={`nis2-rec-${r.priority}`}>
                                            <span className="inline-flex shrink-0 h-6 w-6 items-center justify-center rounded-full bg-accent-cyan/15 border border-accent-cyan/30 text-accent-cyan text-[11px] font-mono">{r.priority}</span>
                                            <div>
                                                <p className="text-[11.5px] font-mono uppercase tracking-[0.18em] text-text-dim">{r.area}</p>
                                                <p className="text-text-primary mt-0.5">{r.action}</p>
                                            </div>
                                        </li>
                                    ))}
                                </ol>
                            </div>

                            {/* Roadmap */}
                            <div className="rounded-xl border border-white/[0.06] bg-surface/40 p-5">
                                <div className="flex items-center gap-2 mb-3">
                                    <Calendar className="w-4 h-4 text-accent-cyan" />
                                    <h3 className="font-display font-semibold text-[14px]">30 / 60 / 90 / 180 day roadmap</h3>
                                </div>
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                    {result.roadmap.map((b, i) => (
                                        <div key={i} className="rounded-md border border-white/[0.06] bg-canvas/40 p-3">
                                            <p className="text-[11.5px] font-mono uppercase tracking-[0.22em] text-accent-cyan">{b.window}</p>
                                            <ul className="mt-2 space-y-1 text-[12.5px]">
                                                {b.items.map((it, j) => (
                                                    <li key={j}>
                                                        <span className="text-text-dim font-mono text-[10.5px]">{it.area}</span>
                                                        <span className="text-text-muted"> — {it.action}</span>
                                                    </li>
                                                ))}
                                            </ul>
                                        </div>
                                    ))}
                                </div>
                            </div>

                            {/* CTA */}
                            <div className="rounded-xl border border-accent-cyan/30 bg-accent-cyan/[0.05] p-5 flex items-center justify-between flex-wrap gap-3">
                                <div>
                                    <p className="font-display font-semibold text-[14px]">Need a hand getting from {result.maturity_level} to Managed?</p>
                                    <p className="mt-1 text-[12.5px] text-text-muted">A 30-min readiness call with a Cybersecurity Forefront engineer turns these gaps into a fixed-price NIS2 program.</p>
                                </div>
                                <Link to="/contact" className="inline-flex items-center gap-1.5 px-4 py-2 rounded-md bg-accent-cyan text-canvas text-[12.5px] font-medium hover:shadow-glow-cyan">
                                    Book a 30-min call <ArrowRight className="w-3 h-3" />
                                </Link>
                            </div>
                        </motion.section>
                    )}
                </AnimatePresence>

                {/* PDF gate modal (anonymous + no email yet) */}
                <AnimatePresence>
                    {pdfGateOpen && (
                        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                            className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center px-4"
                            onClick={() => setPdfGateOpen(false)}
                            data-testid="nis2-pdf-gate-modal">
                            <motion.div onClick={(e) => e.stopPropagation()}
                                initial={{ scale: 0.94, opacity: 0 }} animate={{ scale: 1, opacity: 1 }}
                                className="bg-surface border border-white/[0.08] rounded-2xl p-6 max-w-md w-full shadow-heavy-popover">
                                <div className="flex items-center gap-2 mb-2">
                                    <Download className="w-4 h-4 text-accent-cyan" />
                                    <h3 className="font-display font-semibold text-[16px]">Send me the full PDF report</h3>
                                </div>
                                <p className="text-[12.5px] text-text-muted">
                                    The 5-page roadmap (30/60/90/180 day plan + prioritized actions) will download immediately and we'll also email it to you.
                                </p>
                                <form onSubmit={submitGate} className="mt-5 space-y-3" data-testid="nis2-pdf-gate-form">
                                    <Field label="Email *" type="email" value={pdfGateForm.email}
                                        onChange={(v) => setPdfGateForm({ ...pdfGateForm, email: v })} testId="nis2-pdf-gate-email" />
                                    <Field label="Your name" value={pdfGateForm.name}
                                        onChange={(v) => setPdfGateForm({ ...pdfGateForm, name: v })} testId="nis2-pdf-gate-name" />
                                    <Field label="Company" value={pdfGateForm.company}
                                        onChange={(v) => setPdfGateForm({ ...pdfGateForm, company: v })} testId="nis2-pdf-gate-company" />
                                    <div className="flex justify-end gap-2 pt-2">
                                        <button type="button" onClick={() => setPdfGateOpen(false)}
                                            className="px-3.5 py-1.5 rounded-md border border-white/[0.08] text-[12.5px] text-text-muted hover:text-text-primary">
                                            Cancel
                                        </button>
                                        <button type="submit" disabled={pdfGateBusy || !pdfGateForm.email.trim()}
                                            data-testid="nis2-pdf-gate-submit"
                                            className="inline-flex items-center gap-1.5 px-4 py-1.5 rounded-md bg-accent-cyan text-canvas text-[12.5px] font-medium hover:shadow-glow-cyan disabled:opacity-50">
                                            {pdfGateBusy ? <Loader2 className="w-3 h-3 animate-spin" /> : <Download className="w-3 h-3" />}
                                            Send & download
                                        </button>
                                    </div>
                                </form>
                            </motion.div>
                        </motion.div>
                    )}
                </AnimatePresence>

                {/* SEO content */}
                <section className="mt-16 prose prose-invert max-w-none">
                    <h2 className="font-display font-semibold text-2xl tracking-tight">About the NIS2 Directive</h2>
                    <p className="text-[14px] text-text-muted leading-relaxed mt-3">
                        The EU NIS2 Directive (effective from October 2024) raises the cybersecurity bar for medium and large
                        organizations across 18 sectors — energy, transport, banking, health, water, digital infrastructure,
                        public administration, and more. Article 21 lists ten mandatory risk-management measures. Article 23
                        sets the incident-reporting clock (24h / 72h / 1 month). Sanctions reach <strong>€10M or 2% of global turnover</strong> for
                        essential entities, with potential personal liability for management.
                    </p>
                    <h3 className="mt-6 font-display font-semibold text-xl">Frequently asked questions</h3>
                    <div className="mt-3 space-y-2">
                        {FAQ.map((f, i) => (
                            <div key={i} className="rounded-md border border-white/[0.06] bg-surface/30">
                                <button type="button" onClick={() => setOpenFaq(openFaq === i ? null : i)}
                                    className="w-full flex items-center justify-between px-4 py-3 text-left">
                                    <span className="text-[13.5px] font-medium">{f.q}</span>
                                    {openFaq === i ? <ChevronUp className="w-4 h-4 text-text-dim" /> : <ChevronDown className="w-4 h-4 text-text-dim" />}
                                </button>
                                {openFaq === i && <div className="px-4 pb-3 text-[13px] text-text-muted">{f.a}</div>}
                            </div>
                        ))}
                    </div>
                </section>
            </div>
        </div>
    );
};

const Field = ({ label, value, onChange, testId, type = "text", cls = "" }) => (
    <label className={`block ${cls}`}>
        <span className="text-[10.5px] font-mono uppercase tracking-[0.18em] text-text-muted">{label}</span>
        <input type={type} value={value} onChange={(e) => onChange(e.target.value)} data-testid={testId}
            className="mt-1.5 w-full px-3 py-2 bg-canvas border border-white/[0.08] rounded-md text-[13.5px] text-text-primary focus:outline-none focus:border-accent-cyan" />
    </label>
);

export default Nis2;
